import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { 
  Organization, 
  User, 
  MemberLimitInfo, 
  InvitationVerificationResponse, 
  AcceptInvitationResponse
} from '../../types';
import { OrganizationRole, AcceptInvitationParams } from '../../services/api/organizationService';
import * as organizationService from '../../services/api/organizationService';

// 組織状態の型定義
interface OrganizationState {
  organization: Organization | null;
  members: User[];
  memberLimitInfo: MemberLimitInfo | null;
  invitation: InvitationVerificationResponse | null;
  invitationAccepted: boolean;
  isLoading: boolean;
  error: string | null;
}

// 初期状態
const initialState: OrganizationState = {
  organization: null,
  members: [],
  memberLimitInfo: null,
  invitation: null,
  invitationAccepted: false,
  isLoading: false,
  error: null
};

// 組織情報取得アクション
export const fetchOrganization = createAsyncThunk(
  'organization/fetchOrganization',
  async (_, { rejectWithValue }) => {
    try {
      const organization = await organizationService.getOrganization();
      return organization;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || '組織情報の取得に失敗しました');
    }
  }
);

// メンバー上限情報の取得アクション
export const fetchMemberLimitInfo = createAsyncThunk(
  'organization/fetchMemberLimitInfo',
  async (organizationId: number, { rejectWithValue }) => {
    try {
      const limitInfo = await organizationService.getMemberLimitInfo(organizationId);
      return limitInfo;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'メンバー上限情報の取得に失敗しました');
    }
  }
);

// メンバー追加アクション
interface AddMemberParams {
  organizationId: number;
  userId: number;
  role?: OrganizationRole;
}

export const addMember = createAsyncThunk(
  'organization/addMember',
  async (params: AddMemberParams, { rejectWithValue }) => {
    try {
      const { organizationId, userId, role = 'member' } = params;
      const response = await organizationService.addMember(organizationId, userId, role);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'メンバーの追加に失敗しました');
    }
  }
);

// メンバー削除アクション
export const removeMember = createAsyncThunk(
  'organization/removeMember',
  async ({ organizationId, userId }: { organizationId: number; userId: number }, { rejectWithValue }) => {
    try {
      const response = await organizationService.removeMember(organizationId, userId);
      return { userId, response };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'メンバーの削除に失敗しました');
    }
  }
);

// メンバーの役割更新アクション
export const updateMemberRole = createAsyncThunk(
  'organization/updateMemberRole',
  async (
    { organizationId, userId, role }: { organizationId: number; userId: number; role: OrganizationRole },
    { rejectWithValue }
  ) => {
    try {
      const user = await organizationService.updateMemberRole(organizationId, userId, role);
      return user;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'メンバーの役割更新に失敗しました');
    }
  }
);

// メンバー上限を更新するアクション
export const updateMemberLimit = createAsyncThunk(
  'organization/updateMemberLimit',
  async (
    { organizationId, memberLimit }: { organizationId: number; memberLimit: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await organizationService.updateMemberLimit(organizationId, memberLimit);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'メンバー上限の更新に失敗しました');
    }
  }
);

// メンバーを招待する
export const inviteMember = createAsyncThunk(
  'organization/inviteMember',
  async ({ email, name }: { email: string; name?: string }, { rejectWithValue }) => {
    try {
      const response = await organizationService.inviteMember({ email, name });
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'メンバーの招待に失敗しました');
    }
  }
);

// 招待を検証する
export const verifyInvitation = createAsyncThunk(
  'organization/verifyInvitation',
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await organizationService.verifyInvitation(token);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || '招待の検証に失敗しました');
    }
  }
);

// 招待を承認する
export const acceptInvitation = createAsyncThunk(
  'organization/acceptInvitation',
  async (params: organizationService.AcceptInvitationParams, { rejectWithValue }) => {
    try {
      const response = await organizationService.acceptInvitation(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || '招待の承認に失敗しました');
    }
  }
);

// 組織スライス
const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    clearOrganizationError: (state) => {
      state.error = null;
    },
    resetOrganizationState: () => initialState
  },
  extraReducers: (builder) => {
    // 組織情報取得
    builder
      .addCase(fetchOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organization = action.payload;
        if (action.payload.users) {
          state.members = action.payload.users;
        }
        state.error = null;
      })
      .addCase(fetchOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

    // メンバー上限情報取得
      .addCase(fetchMemberLimitInfo.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMemberLimitInfo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.memberLimitInfo = action.payload;
        state.error = null;
      })
      .addCase(fetchMemberLimitInfo.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

    // メンバー追加
      .addCase(addMember.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addMember.fulfilled, (state, action) => {
        state.isLoading = false;
        // 追加されたメンバーをリストに追加
        state.members.push(action.payload);
        state.error = null;
      })
      .addCase(addMember.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

    // メンバー削除
      .addCase(removeMember.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeMember.fulfilled, (state, action) => {
        state.isLoading = false;
        // 削除されたメンバーをリストから除外
        state.members = state.members.filter(member => member.id !== action.payload.userId);
        state.error = null;
      })
      .addCase(removeMember.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

    // メンバーロール更新
      .addCase(updateMemberRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateMemberRole.fulfilled, (state, action) => {
        state.isLoading = false;
        // 更新されたユーザーの情報を更新
        const index = state.members.findIndex(member => member.id === action.payload.id);
        if (index !== -1) {
          state.members[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateMemberRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

    // メンバー上限更新
      .addCase(updateMemberLimit.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateMemberLimit.fulfilled, (state, action) => {
        state.isLoading = false;
        // 組織情報が返ってきた場合は更新
        if (action.payload.organization) {
          state.organization = action.payload.organization;
          
          // メンバー上限情報も更新
          if (state.memberLimitInfo) {
            state.memberLimitInfo.member_limit = action.payload.organization.member_limit;
          }
        }
        state.error = null;
      })
      .addCase(updateMemberLimit.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

    // メンバー招待
      .addCase(inviteMember.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(inviteMember.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(inviteMember.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

    // 招待検証
      .addCase(verifyInvitation.pending, (state) => {
        state.isLoading = true;
        state.invitation = null;
        state.error = null;
      })
      .addCase(verifyInvitation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.invitation = action.payload;
        state.error = null;
      })
      .addCase(verifyInvitation.rejected, (state, action) => {
        state.isLoading = false;
        state.invitation = null;
        state.error = action.payload as string;
      })

    // 招待承認
      .addCase(acceptInvitation.pending, (state) => {
        state.isLoading = true;
        state.invitationAccepted = false;
        state.error = null;
      })
      .addCase(acceptInvitation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.invitationAccepted = true;
        // 必要に応じて、ユーザー情報や組織情報を更新
        state.error = null;
      })
      .addCase(acceptInvitation.rejected, (state, action) => {
        state.isLoading = false;
        state.invitationAccepted = false;
        state.error = action.payload as string;
      });
  }
});

// セレクター
export const selectOrganization = (state: RootState) => state.organization.organization;
export const selectMembers = (state: RootState) => state.organization.members;
export const selectMemberLimitInfo = (state: RootState) => state.organization.memberLimitInfo;
export const selectIsLoading = (state: RootState) => state.organization.isLoading;
export const selectError = (state: RootState) => state.organization.error;
export const selectInvitation = (state: RootState) => state.organization.invitation;
export const selectInvitationAccepted = (state: RootState) => state.organization.invitationAccepted;

// メンバーにリーダー権限があるかチェックするセレクタ
export const selectIsUserLeader = (state: RootState) => {
  const authData = state.auth.user;
  
  // ネストされたユーザーオブジェクト構造に対応
  // 型安全のために型アサーションを使用
  const currentUser = (authData as any)?.user || authData;
  console.log('selectIsUserLeader - currentUser object:', currentUser);
  
  if (!currentUser) return false;
  
  // 1. 認証ユーザー自身のroleフィールドをチェック
  if (currentUser.role === 'leader') {
    console.log('User has leader role directly in auth state');
    return true;
  }
  
  // 2. 組織のメンバーリストから該当ユーザーのロールをチェック
  // 組織データが読み込まれていない場合に対応
  if (state.organization.members && state.organization.members.length > 0) {
    const member = state.organization.members.find(m => m.id === currentUser.id);
    if (member?.role === 'leader') {
      console.log('User has leader role in members list');
      return true;
    }
  }
  
  // 3. 組織オブジェクトのusersリストからもチェック
  if (state.organization.organization?.users) {
    const orgMember = state.organization.organization.users.find(u => u.id === currentUser.id);
    if (orgMember?.role === 'leader') {
      console.log('User has leader role in organization users');
      return true;
    }
  }
  
  return false;
};

export const { clearOrganizationError, resetOrganizationState } = organizationSlice.actions;

export default organizationSlice.reducer;
